import { formatPrice } from "@/lib/format";
import type { GroupPrice } from "@/lib/dto";

type Props = { groupPrices: GroupPrice[]; currency: string };

function bracketLabel(groupPrice: GroupPrice): string {
  return groupPrice.minPerson === groupPrice.maxPerson
    ? `${groupPrice.minPerson} Pax`
    : `${groupPrice.minPerson} - ${groupPrice.maxPerson} Pax`;
}

/**
 * "Group-Size Discounts" table — matches production's real markup exactly
 * (confirmed via the live DOM): a "No. of Persons / Price per Person"
 * header row over rows joined by a dashed leader line, not a bordered card.
 *
 * The backend returns overlapping/duplicate brackets for some packages
 * (e.g. two separate `1–1 pax` rows at different prices); production shows
 * one row per distinct bracket, so rows are de-duplicated by their
 * min–max range, keeping the lowest price for each.
 */
export function GroupPricingDisplay({ groupPrices, currency }: Props) {
  if (!groupPrices || groupPrices.length < 2) return null;

  const byBracket = new Map<string, GroupPrice>();
  for (const groupPrice of groupPrices) {
    const key = `${groupPrice.minPerson}-${groupPrice.maxPerson}`;
    const existing = byBracket.get(key);
    if (!existing || groupPrice.price < existing.price) {
      byBracket.set(key, groupPrice);
    }
  }
  const rows = [...byBracket.values()].sort(
    (a, b) => (a.minPerson ?? 0) - (b.minPerson ?? 0),
  );
  if (rows.length < 2) return null;

  return (
    <div className="font-secondary text-[0.9375rem] font-medium lg:pl-3">
      <ul className="[&>li+li]:mt-1">
        <li className="mb-3 flex items-center justify-between border-b border-b-primary pb-1 font-bold text-headings">
          <span>No. of Persons</span>
          <span>Price per Person</span>
        </li>
        {rows.map((groupPrice, index) => (
          <li
            key={groupPrice.id ?? index}
            className="relative z-10 flex items-center justify-between before:absolute before:inset-x-0 before:top-1/2 before:-z-10 before:h-px before:-translate-y-1/2 before:border-t before:border-dashed before:border-t-primary/50"
          >
            <span className="inline-block bg-bg pr-3 lg:bg-white">
              {bracketLabel(groupPrice)}
            </span>
            <span className="inline-block bg-bg pl-3 lg:bg-white">
              {formatPrice(groupPrice.price, groupPrice.currency || currency)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
